import { Alert, Button, Input, Popover, Select } from 'antd';
import { useCallback, useState } from 'react';
import { useRemoteSearchOptions } from '../../../shared/hooks/use-remote-search-options';
import { useSubmission } from '../../../shared/hooks/use-submission';
import { apiErrorMessage } from '../../../shared/policies/api-error-message';
import { listAlertAssignees, type AlertView } from '../alerts.api';

export type ReassignAlert = (alert: AlertView, assignee: { id: string; name: string }, note?: string) => Promise<void>;

interface ReassignAlertPopoverProps {
  alert: AlertView;
  onReassign: ReassignAlert;
}

/** The people who can answer alerts, first page open and searchable. The form mounts only when the popover opens, so a table of rows does not fetch it per row. */
function ReassignForm({ alert, onReassign, onDone }: ReassignAlertPopoverProps & { onDone: () => void }) {
  const fetchAssignees = useCallback(
    async (q: string | undefined) => (await listAlertAssignees(q)).map((person) => ({ value: person.id, label: person.name })),
    [],
  );
  const { selectProps } = useRemoteSearchOptions(fetchAssignees);
  const [assigneeId, setAssigneeId] = useState<string | undefined>(undefined);
  const [note, setNote] = useState('');
  const { error, submitting, submit } = useSubmission();
  const options = selectProps.options.filter((option) => option.value !== alert.ownerId);

  async function confirm() {
    const assignee = options.find((option) => option.value === assigneeId);
    if (!assignee) return;
    const done = await submit(
      () => onReassign(alert, { id: assignee.value, name: assignee.label }, note.trim() || undefined).then(() => true),
      (err) => apiErrorMessage(err) ?? 'That did not go through. Try again.',
    );
    if (done) onDone();
  }

  return (
    <div className="flex w-72 flex-col gap-3">
      {error && <Alert type="error" title={error} showIcon />}
      <Select {...selectProps} options={options} value={assigneeId} onChange={setAssigneeId} placeholder="Who should have it" aria-label="Reassign to" />
      <Input.TextArea rows={2} maxLength={500} value={note} onChange={(event) => setNote(event.target.value)} placeholder="Note (optional)" aria-label="Note" />
      <Button type="primary" disabled={!assigneeId} loading={submitting} onClick={confirm}>
        Reassign
      </Button>
    </div>
  );
}

/** Hands an alert to someone who can answer it (gitaumoses4/chukua#99). Acknowledging does not do this: the owner is who the alert was handed to. */
export function ReassignAlertPopover({ alert, onReassign }: ReassignAlertPopoverProps) {
  const [open, setOpen] = useState(false);
  return (
    <Popover
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
      destroyOnHidden
      title={`Reassign ${alert.ruleName} on ${alert.truckRegistration}`}
      content={<ReassignForm alert={alert} onReassign={onReassign} onDone={() => setOpen(false)} />}
    >
      <Button size="small">Reassign</Button>
    </Popover>
  );
}
